/*
  @layer  core
  @name   Révélation progressive
  @deps   aucune
  @i18n   le libellé est fourni par l'appelant

  Révèle N éléments masqués à chaque clic, puis se retire quand il n'en reste plus.

  Pourquoi pas `show-more.js` de Dawn : son composant bascule **tous** les éléments d'un coup,
  et impose une structure DOM précise (`.parent-display`, `.parent-wrap`, `[id^="Show-More-"]`,
  `.label-text`). Reproduire ce contrat pour obtenir un comportement différent aurait coûté plus
  que de l'écrire.

  Usage :
    <ul id="MaListe"> …
      <li data-show-more-item hidden>…</li>
    </ul>
    <enet-show-more data-target="MaListe" data-step="3">
      <button type="button">Afficher plus</button>
    </enet-show-more>

  Accessibilité : le focus se déplace sur le premier élément révélé. Sans ça, un utilisateur au
  clavier clique et rien ne lui indique que du contenu est apparu plus haut dans la page.

  Dégradation sans JavaScript : les éléments au-delà du seuil restent masqués et le bouton ne
  fait rien. C'est le compromis assumé — l'alternative, tout afficher puis masquer en CSS,
  imposerait à tous de télécharger un contenu que presque personne ne déplie.

  Charger le script avec `defer`. Sans lui, un `<script>` placé avant l'élément dans le document
  s'exécute pendant l'analyse : l'élément est alors amélioré avant que ses enfants existent.
  L'écouteur délégué ci-dessous rend ce cas inoffensif, mais `defer` reste la bonne pratique.
*/

if (!customElements.get('enet-show-more')) {
  customElements.define(
    'enet-show-more',
    class EnetShowMore extends HTMLElement {
      connectedCallback() {
        this.step = parseInt(this.dataset.step, 10) || 3;

        // L'écouteur est posé sur l'hôte, pas sur le bouton. Un élément personnalisé peut être
        // amélioré **avant** que ses enfants soient analysés — si le script s'exécute plus haut
        // dans le document, `querySelector('button')` renvoie null et l'écouteur n'est jamais
        // attaché, sans la moindre erreur. L'hôte, lui, existe toujours.
        this.addEventListener('click', (event) => {
          if (event.target.closest('button')) this.reveal();
        });
      }

      get hiddenItems() {
        const target = document.getElementById(this.dataset.target);
        if (!target) return [];
        return Array.from(target.querySelectorAll('[data-show-more-item][hidden]'));
      }

      reveal() {
        const revealed = this.hiddenItems.slice(0, this.step);
        revealed.forEach((item) => item.removeAttribute('hidden'));

        if (this.hiddenItems.length === 0) this.hidden = true;

        const first = revealed[0];
        if (!first) return;

        // `<summary>` est focusable nativement ; sinon on rend l'élément focusable le temps
        // d'y poser le focus, sans l'ajouter durablement à l'ordre de tabulation.
        const focusTarget = first.querySelector('summary') || first;
        if (focusTarget === first) first.setAttribute('tabindex', '-1');
        focusTarget.focus();
      }
    }
  );
}
